import { lazy, Suspense, useState, useEffect, startTransition, useContext } from 'react';
import { StepperContainer, StepperProvider } from '../StepperContainer';
import { useAuth } from '../../../../store/auth';
import { useAppSelector } from '../../../../hooks/hooksRedux';
import { useTranslation } from 'react-i18next';
import { AnnualRenewalIntroPage } from './AnnualRenewalIntroPage';
import { DashboardCustomerContext } from '../../Context';

const AnnualFeeIntroPage = lazy(() => import('../AnnualFeeIntroPage'));
const OnboardingAddonsPage = lazy(() => import('../AddonsPage'));
const OnboardingSummaryPage = lazy(() => import('../SummaryPage'));

interface Step {
  id: string;
  order: number;
}

export const AnnualRenewalOnboarding = () => {
  const { t } = useTranslation();
  const { token, company } = useAuth();
  const { legalPersonalitySlug } = useAppSelector((state) => state.language);
  const { latestAnnualFee, refreshLatestAnnualFee } = useContext(DashboardCustomerContext);

  const [showIntro, setShowIntro] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkDueDate = async () => {
      if (!company || !token) {
        startTransition(() => {
          setShowIntro(true);
          setLoading(false);
        });
        return;
      }

      try {
        const data = latestAnnualFee ?? (await refreshLatestAnnualFee());

        if (!data) {
          startTransition(() => {
            setShowIntro(true);
            setLoading(false);
          });
          return;
        }

        const dueDateStr = data?.due_date;
        const status = data?.status;

        if (!dueDateStr) {
          startTransition(() => {
            setShowIntro(true);
            setLoading(false);
          });
          return;
        }

        if (status === 'paid') {
          startTransition(() => {
            setShowIntro(false);
            setLoading(false);
          });
          return;
        }

        const dueDate = new Date(dueDateStr);
        const now = new Date();
        dueDate.setHours(0, 0, 0, 0);
        now.setHours(0, 0, 0, 0);
        const diffTime = dueDate.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        startTransition(() => {
          setShowIntro(diffDays > 90);
          setLoading(false);
        });
      } catch (e) {
        console.error('Error checking due date:', e);
        startTransition(() => {
          setShowIntro(true);
          setLoading(false);
        });
      }
    };

    checkDueDate();
  }, [company, token, legalPersonalitySlug, latestAnnualFee, refreshLatestAnnualFee]);

  const [steps] = useState<Step[]>([
    { id: 'intro-step', order: 1 },
    { id: 'annual-fee-step', order: 2 },
    { id: 'addons-step', order: 3 },
    { id: 'summary-step', order: 4 }
  ]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('labels.lblLoading')}...</p>
        </div>
      </div>
    );
  }

  // Determina os steps dinamicamente baseado no showIntro
  const dynamicSteps = showIntro 
    ? [
        { id: 'intro-step', order: 1 },
        { id: 'annual-fee-step', order: 2 },
        { id: 'addons-step', order: 3 },
        { id: 'summary-step', order: 4 }
      ]
    : [
        { id: 'annual-fee-step', order: 1 },
        { id: 'addons-step', order: 2 },
        { id: 'summary-step', order: 3 }
      ];

  // Determina o step inicial baseado no showIntro
  const initialStep = showIntro ? 'intro-step' : 'annual-fee-step';

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <StepperProvider 
        steps={dynamicSteps} 
        initialStep={initialStep}
      >
        {showIntro && <StepperContainer step="intro-step" component={AnnualRenewalIntroPage} />}
        <StepperContainer step="annual-fee-step" component={AnnualFeeIntroPage} />
        <StepperContainer step="addons-step" component={OnboardingAddonsPage} />
        <StepperContainer step="summary-step" component={OnboardingSummaryPage} />
      </StepperProvider>
    </Suspense>
  );
};
